/**
 * @typedef {Object} Job
 * @property {string} [title]
 * @property {string} [department]
 * @property {string} [location]
 * @property {string} [city]
 * @property {string} [country]
 * @property {string} [salaryRange]
 * @property {string} [closingDate]
 * @property {string} [description]
 * @property {string} [applyUrl]
 * @property {string} [jobUrl]
 */

const DEFAULT_TEXT = {
  apply: 'Apply',
  empty: 'There are no vacancies at the moment.',
  error: 'We couldn’t load our current vacancies. Please try again later.',
};

/**
 * The labelled rows shown above each job description, in the order they are rendered.
 *
 * @type {Array<{ label: string, value: (job: Job) => string | undefined }>}
 */
const META_FIELDS = [
  { label: 'Department', value: (job) => job.department },
  { label: 'Location', value: (job) => job.location || job.city },
  { label: 'Country', value: (job) => job.country },
  { label: 'Salary', value: (job) => job.salaryRange },
  { label: 'Closing date', value: (job) => job.closingDate },
];

/**
 * Elements kept when rendering the feed's description. Anything else is unwrapped, so its text
 * survives while scripts, event handlers and embedded media never reach the page.
 */
const ALLOWED_TAGS = new Set([
  'A',
  'B',
  'BR',
  'CAPTION',
  'EM',
  'I',
  'LI',
  'OL',
  'P',
  'STRONG',
  'TABLE',
  'TBODY',
  'TD',
  'TFOOT',
  'TH',
  'THEAD',
  'TR',
  'UL',
]);
const HEADING_TAGS = new Set(['H1', 'H2', 'H3', 'H4', 'H5', 'H6']);
const DISCARDED_TAGS = new Set(['IFRAME', 'NOSCRIPT', 'OBJECT', 'SCRIPT', 'STYLE', 'TEMPLATE']);
const SAFE_PROTOCOLS = ['http:', 'https:', 'mailto:'];

/**
 * The job board writes section headings as <h1>. They become emphasised paragraphs instead, so a
 * job description cannot introduce a second <h1> to the page.
 *
 * @param {Element} heading
 */
function demoteHeading(heading) {
  const paragraph = document.createElement('p');
  const strong = document.createElement('strong');

  strong.append(...Array.from(heading.childNodes));
  paragraph.append(strong);
  heading.replaceWith(paragraph);
}

/**
 * @param {string} href
 * @returns {boolean}
 */
function isSafeHref(href) {
  try {
    return SAFE_PROTOCOLS.includes(new URL(href, window.location.href).protocol);
  } catch {
    return false;
  }
}

/**
 * @param {string} value
 * @returns {boolean}
 */
function isSpan(value) {
  return /^[1-9]\d*$/.test(value);
}

/**
 * @param {string} value
 * @returns {boolean}
 */
function isScope(value) {
  return ['row', 'col', 'rowgroup', 'colgroup'].includes(value);
}

/**
 * Attributes kept per element, each paired with the test its value has to pass. Table cells keep
 * their spans, since dropping those would silently reshape the table.
 *
 * @type {Record<string, Record<string, (value: string) => boolean>>}
 */
const ALLOWED_ATTRIBUTES = {
  A: { href: isSafeHref },
  TD: { colspan: isSpan, rowspan: isSpan },
  TH: { colspan: isSpan, rowspan: isSpan, scope: isScope },
};

/**
 * Strips a parsed description down to {@link ALLOWED_TAGS} and {@link ALLOWED_ATTRIBUTES}. Children
 * are handled first so an unwrapped element leaves sanitized content behind.
 *
 * @param {Element} root
 */
function sanitize(root) {
  Array.from(root.children).forEach((element) => {
    sanitize(element);

    if (DISCARDED_TAGS.has(element.tagName)) {
      element.remove();
      return;
    }

    if (HEADING_TAGS.has(element.tagName)) {
      demoteHeading(element);
      return;
    }

    if (!ALLOWED_TAGS.has(element.tagName)) {
      element.replaceWith(...Array.from(element.childNodes));
      return;
    }

    const allowed = ALLOWED_ATTRIBUTES[element.tagName] ?? {};

    Array.from(element.attributes).forEach(({ name, value }) => {
      if (!allowed[name]?.(value)) element.removeAttribute(name);
    });

    if (element instanceof HTMLAnchorElement && element.hasAttribute('href')) {
      element.target = '_blank';
      element.rel = 'noopener';
    }
  });
}

/**
 * @param {string} tag
 * @param {string} [className]
 * @param {string} [text]
 * @returns {HTMLElement}
 */
function createElement(tag, className, text) {
  const element = document.createElement(tag);

  if (className) element.className = className;
  if (text) element.textContent = text;

  return element;
}

if (!customElements.get('careers-feed')) {
  customElements.define(
    'careers-feed',
    class CareersFeed extends HTMLElement {
      connectedCallback() {
        if (this.loaded) return;

        this.loaded = true;

        this.load();
      }

      async load() {
        const { feedUrl } = this.dataset;

        if (!feedUrl) return;

        try {
          const response = await fetch(feedUrl, { headers: { Accept: 'application/json' } });

          if (!response.ok) throw new Error(`Request failed with status ${response.status}`);

          const { jobs } = await response.json();

          if (!Array.isArray(jobs) || jobs.length === 0) {
            this.setStatus(this.text.empty);
            return;
          }

          this.render(jobs);
        } catch (error) {
          console.error('[careers-feed]', error);

          this.setStatus(this.text.error);
        }
      }

      /**
       * @param {Array<Job>} jobs
       */
      render(jobs) {
        const list = this.list;
        const accordion = this.template?.content.querySelector('site-accordion');

        if (!list || !accordion) {
          throw new Error(`Missing ${list ? 'accordion template' : 'job list container'} in the block markup`);
        }

        const fragment = document.createDocumentFragment();

        jobs.forEach((job) => fragment.append(this.buildJob(job, accordion)));

        list.replaceChildren(fragment);
        list.hidden = false;

        if (this.status) this.status.hidden = true;
      }

      /**
       * @param {string} message
       */
      setStatus(message) {
        if (!this.status) return;

        this.status.textContent = message;
        this.status.hidden = false;
      }

      /**
       * Clones the accordion rendered by the `accordion` snippet, so the job list stays in sync
       * with the markup that snippet produces.
       *
       * @param {Job} job
       * @param {Element} accordionTemplate
       * @returns {Element}
       */
      buildJob(job, accordionTemplate) {
        const accordion = /** @type {Element} */ (accordionTemplate.cloneNode(true));
        const title = job.title || '';
        const location = job.location || job.city;

        // The snippet's script tag never ran inside the template, so it would execute on insert.
        accordion.querySelector('script')?.remove();

        const titleText = accordion.querySelector('.accordion__title-text');
        if (titleText) titleText.textContent = title;

        const subTitle = accordion.querySelector('.accordion__sub-title');
        if (subTitle && location) subTitle.textContent = location;
        else subTitle?.remove();

        const button = accordion.querySelector('.accordion__title');
        if (button) button.setAttribute('data-title', title);

        const content = accordion.querySelector('[data-careers-job-content]');
        if (content) content.replaceChildren(...this.buildJobContent(job));

        return accordion;
      }

      /**
       * Everything but the title, in render order: the description, the meta list and the apply
       * button.
       *
       * @param {Job} job
       * @returns {Array<HTMLElement>}
       */
      buildJobContent(job) {
        /**
         * @type {Array<HTMLElement>}
         */
        const content = [];

        const meta = this.buildMeta(job);
        const description = this.buildDescription(job);
        const applyLink = this.buildApplyLink(job);

        if (description) content.push(description);
        if (meta) content.push(meta);
        if (applyLink) content.push(applyLink);

        return content;
      }

      /**
       * @param {Job} job
       * @returns {HTMLElement | null}
       */
      buildMeta(job) {
        const rows = META_FIELDS.map((field) => ({ label: field.label, value: field.value(job) })).filter(
          (row) => row.value,
        );

        if (rows.length === 0) return null;

        const meta = createElement('dl', 'block-careers-feed__job-meta');

        rows.forEach((row) => {
          meta.append(createElement('dt', 'block-careers-feed__job-meta-label', row.label));
          meta.append(createElement('dd', 'block-careers-feed__job-meta-value', row.value));
        });

        return meta;
      }

      /**
       * The feed returns HTML written in a third-party job board, so it is parsed in an inert
       * document and reduced to an allowlist before it is put on the page.
       *
       * @param {Job} job
       * @returns {HTMLElement | null}
       */
      buildDescription(job) {
        if (!job.description) return null;

        const parsed = new DOMParser().parseFromString(job.description, 'text/html').body;

        sanitize(parsed);

        parsed.querySelectorAll('p').forEach((paragraph) => {
          if (!paragraph.textContent?.trim()) paragraph.remove();
        });

        const opening = parsed.firstElementChild;
        if (job.title && opening?.textContent?.trim() === job.title.trim()) opening.remove();

        parsed.querySelectorAll('table').forEach((table) => {
          const scroller = createElement('div', 'block-careers-feed__table');

          table.replaceWith(scroller);
          scroller.append(table);
        });

        if (!parsed.textContent?.trim()) return null;

        const description = createElement('div', 'block-careers-feed__job-description');

        description.append(...Array.from(parsed.childNodes));

        return description;
      }

      /**
       * @param {Job} job
       * @returns {HTMLAnchorElement | null}
       */
      buildApplyLink(job) {
        const href = job.applyUrl || job.jobUrl;

        if (!href) return null;

        const link = document.createElement('a');

        link.className =
          'block-careers-feed__job-apply button button--secondary button--48 button--default font-mobile-ui-14 font-desktop-ui-14';
        link.href = href;
        link.target = '_blank';
        link.rel = 'noopener';
        link.textContent = this.text.apply;

        return link;
      }

      get text() {
        return {
          apply: this.dataset.applyLabel || DEFAULT_TEXT.apply,
          empty: this.dataset.emptyText || DEFAULT_TEXT.empty,
          error: this.dataset.errorText || DEFAULT_TEXT.error,
        };
      }

      /**
       * @returns {HTMLElement | null}
       */
      get list() {
        return this.querySelector('[data-careers-list]');
      }

      /**
       * @returns {HTMLTemplateElement | null}
       */
      get template() {
        return this.querySelector('template[data-careers-template]');
      }

      /**
       * @returns {HTMLElement | null}
       */
      get status() {
        return this.querySelector('[data-careers-status]');
      }
    },
  );
}
